import { makeAutoObservable, observable, ObservableMap } from 'mobx';
import { HttpBackend } from '../../Services/Http/HttpBackend';
import { BaseTrackingKey, TrackingKeysList } from './TrackingKeys';
import { ExerciseId, createExerciseId } from './ExerciseId';
import { v4 as UUID } from 'uuid';
import { BodyPart, createBodyPart } from './BodyPart';
import { Media, createMedia, getMediaSmallest } from '../Media/Media';
import { ExercisePosition, createExercisePosition } from './ExercisePosition';
import { Pageable } from '../Interfaces/Pageable';

export type ExerciseForce = 'PUSH' | 'PULL' | 'PUSH_PULL';
export type ExerciseMechanics = 'ISOLATED' | 'COMPOUND';
export type ExerciseUtility = 'AUXILIARY' | 'BASIC';
export type ExerciseExecutionType = 'ONE_SIDED' | 'BOTH_SIDED' | 'ALTERNATING';
export type SpineForceType = 'stretch' | 'contract';

export type ExerciseVariationRequest = Pageable & {
  query: string;
  lang: string;
  type: string[];
  bodyPartIds: string[];
  excludedBodyPartIds: string[];
  synergistsIds: string[];
  stabilizersIds: string[];
  joints: string[];
  excludedJoints: string[];
  force: string[];
  mechanics: string[];
  utilities: string[];
  primaryPositions: string[];
  secondaryPositions: string[];
  excludedPrimaryPositions: string[];
  excludedSecondaryPositions: string[];
  executionTypes: string[];
  excludedExecutionTypes: string[];
  requiredEquipmentTypes: string[];
  equipmentTypes: string[];
  secondaryEquipmentTypes: string[];
  excludedEquipmentTypes: string[];
  excludeEmptyEquipmentTypes: boolean;
  /**
   * do not enforce that all equipmentTypes are present -> $in
   */
  equipmentTypeMatchNotAll: boolean;
  trackingKeys: string[];
  excludedTrackingKeys: string[];
  tags: string[];
  hipFlexing?: boolean;
  spineFlexing?: boolean;
  spineForceType?: string;
  excludedTags: string[];
  sourceType?: string;
  sourceId?: string;
  branchId?: string;
  available?: boolean;
  spotClassId?: string;
  spotGroupId?: string;
  archived: boolean;
  excludeExerciseIds: string[];
  noAlternatives?: boolean;
  noHarderAlternatives?: boolean;
};

export type ExerciseVariation = {
  id: string;
  name: string;
  names: ObservableMap<string, string>;
  type: string;
  description?: string;
  force?: ExerciseForce;
  mechanics?: ExerciseMechanics;
  utility?: ExerciseUtility;
  met: number;
  bodyParts: BodyPart[];
  synergists: BodyPart[];
  stabilizers: BodyPart[];
  medias: Media[];
  previewMedias: Media[];
  executionTypes: string[];
  executionType?: ExerciseExecutionType;
  sourceType?: string;
  sourceId?: string;
  equipmentTypes: string[];
  secondaryEquipmentTypes: string[];
  initialPosition?: ExercisePosition;
  trackingKeys: BaseTrackingKey[];
  optionalTrackingKeys: BaseTrackingKey[];
  tags: string[];
  hipFlexing: boolean;
  spineFlexing: boolean;
  spineForceType?: SpineForceType;
  easierAlternativeIds: string[];
  alternativeIds: string[];
  harderAlternativeIds: string[];
  readonly videoMedia: Media | undefined;
  readonly previewVideoMedia: Media | undefined;
  readonly imageMedia: Media | undefined;
  readonly firstSmallestMediaUrl: string | undefined;
  readonly exerciseIdentifier: ExerciseId;
  readonly defaultTrackingKeys: BaseTrackingKey[];
  readonly trackingParameters: BaseTrackingKey[];
  readonly tracksDuration: boolean;
  readonly tracksWeightOrReps: boolean;
  readonly sortedTrackingParameters: BaseTrackingKey[];
  readonly isAlternating: boolean;
  readonly isOneSided: boolean;
  localeName(lang: string): string;
};

export function createExerciseVariation(json?: Partial<ExerciseVariation>): ExerciseVariation {
  return makeAutoObservable({
    id: json?.id ?? UUID(),
    name: json?.name ?? '',
    names: observable.map((json as any)?.names ?? {}),
    type: json?.type ?? 'STRENGTH',
    description: json?.description,
    force: json?.force,
    mechanics: json?.mechanics,
    utility: json?.utility,
    met: json?.met ?? 5.5,
    bodyParts: (json?.bodyParts ?? []).map((b: any) => createBodyPart(b)),
    synergists: (json?.synergists ?? []).map((b: any) => createBodyPart(b)),
    stabilizers: (json?.stabilizers ?? []).map((b: any) => createBodyPart(b)),
    medias: (json?.medias ?? []).map((m: any) => createMedia(m)),
    previewMedias: (json?.previewMedias ?? []).map((m: any) => createMedia(m)),
    executionTypes: json?.executionTypes ?? [],
    executionType: json?.executionType,
    sourceType: json?.sourceType,
    sourceId: json?.sourceId,
    equipmentTypes: json?.equipmentTypes ?? [],
    secondaryEquipmentTypes: json?.secondaryEquipmentTypes ?? [],
    initialPosition: json?.initialPosition ? createExercisePosition(json.initialPosition as any) : undefined,
    trackingKeys: json?.trackingKeys ?? [],
    optionalTrackingKeys: json?.optionalTrackingKeys ?? [],
    tags: json?.tags ?? [],
    hipFlexing: json?.hipFlexing ?? false,
    spineFlexing: json?.spineFlexing ?? false,
    spineForceType: json?.spineForceType,
    easierAlternativeIds: json?.easierAlternativeIds ?? [],
    alternativeIds: json?.alternativeIds ?? [],
    harderAlternativeIds: json?.harderAlternativeIds ?? [],

    localeName(lang: string): string {
      if (this.names.has(lang)) {
        return this.names.get(lang) || this.name || this.names.get('de') || '';
      }
      return this.name;
    },

    get videoMedia(): Media | undefined {
      return this.medias.find((m) => m.mediaType.startsWith('video'));
    },

    get previewVideoMedia(): Media | undefined {
      return this.previewMedias.find((m) => m.mediaType.startsWith('video'));
    },

    get imageMedia(): Media | undefined {
      return this.medias.find((m) => m.mediaType.startsWith('image'));
    },

    get firstSmallestMediaUrl(): string | undefined {
      return this.imageMedia ? getMediaSmallest(this.imageMedia) : undefined;
    },

    get exerciseIdentifier(): ExerciseId {
      return createExerciseId({
        variationId: this.id,
        sourceType: this.sourceType,
        sourceId: this.sourceId,
      });
    },

    get defaultTrackingKeys(): BaseTrackingKey[] {
      switch (this.type.toLowerCase()) {
        case 'strength':
          if (this.equipmentTypes.indexOf('FREE') !== -1) {
            return ['REPETITIONS', 'DURATION', 'BREAK'];
          }
          return ['REPETITIONS', 'WEIGHT', 'DURATION', 'BREAK'];
        case 'endurance':
        case 'flexibility':
        case 'balance':
        case 'relax':
        default:
          return ['DURATION', 'BREAK'];
      }
    },

    get trackingParameters(): BaseTrackingKey[] {
      if (this.trackingKeys.length > 0) {
        const hasBreak = this.trackingKeys.indexOf('BREAK') !== -1;
        const hasDuration = this.trackingKeys.indexOf('DURATION') !== -1;
        if (!hasBreak) {
          if (!hasDuration) return this.trackingKeys.concat(['DURATION', 'BREAK']);
          return this.trackingKeys.concat(['BREAK']);
        } else if (!hasDuration) {
          if (!hasBreak) return this.trackingKeys.concat(['DURATION', 'BREAK']);
          return this.trackingKeys.concat(['DURATION']);
        }
        return this.trackingKeys;
      }
      return this.defaultTrackingKeys;
    },

    get tracksDuration(): boolean {
      return this.trackingParameters.indexOf('DURATION') !== -1;
    },

    get tracksWeightOrReps(): boolean {
      return this.trackingParameters.indexOf('WEIGHT') !== -1 || this.trackingParameters.indexOf('REPETITIONS') !== -1;
    },

    get sortedTrackingParameters(): BaseTrackingKey[] {
      return this.trackingParameters.sort((a, b) => TrackingKeysList.indexOf(a) - TrackingKeysList.indexOf(b));
    },

    get isAlternating(): boolean {
      return this.executionType === 'ALTERNATING';
    },

    get isOneSided(): boolean {
      return this.executionType === 'ONE_SIDED';
    },
  });
}

export const ExerciseVariationApi = {
  find(params?: Partial<ExerciseVariationRequest>): Promise<ExerciseVariation[]> {
    return HttpBackend.get('/activity/exercise/v3', params).then((result) =>
      (result ?? []).map((res: any) => createExerciseVariation(res)),
    );
  },

  random(params?: Partial<ExerciseVariationRequest>): Promise<ExerciseVariation[]> {
    return HttpBackend.get('/activity/exercise/v3/random', params).then((result) =>
      (result ?? []).map((res: any) => createExerciseVariation(res)),
    );
  },

  get(id: string): Promise<ExerciseVariation> {
    return HttpBackend.get(`/activity/exercise/v3/variation/${id}`).then((res) => createExerciseVariation(res));
  },
};
